/**
 * 预算契约 —— **G 域**（G1 预算体系 + G2 预警规则 · 规格 **§5** · G 域 PRD **§4** ~ **§7** ·
 * `DR-011`）
 *
 * ## 🔴 四处刻意的形状决定
 *
 * | 决定 | 为什么 |
 * |---|---|
 * | **`currency` 恒为本币**（服务端下发，端侧不自取） | 规格 §5：预算金额**恒为本币**；非本币支出按**交易日期汇率**折本币后计入（`DR-004`）⇒ 端侧拿到的每个数都已经是本币分 |
 * | 🔴 **下发的 `progress` 是「已算好的派生量」，不是原料** | `spent` / `remaining` / `usedE4` / `timeProgressE4` / `deviationE4` / `projectedEnd` / `level` **全部**由服务端经内核算出 ⇒ 端侧**零口径**（不求和、不除、不外推）。这正是 `AC-G2.12`「四消费点逐分恒等」的落地方式 —— 端侧根本没有第二处可算 |
 * | **`UpdateBudgetRequest` **不含** `categoryId` / `period`** | `UNIQUE(ledgerId, categoryId, period)`：改这两项等于换一条预算，唯一索引会在两处打架（先例 = `UpdateScheduleRequest` 不含 `accountId`）。要换 → 删旧建新 |
 * | **`unpaidPending` 与 `spent` 分开下发** | 🔴 待缴·未付**计入** `spent`（口径不开例外），但**必须能标注**（卡面「含待缴 ¥X 未付」，仅 `X > 0` 显示）⇒ 它是**呈现用的拆分**，不是口径的分叉（`DR-011` §4） |
 *
 * ## ⚠️ 「窗口标识」为什么不在这份契约里
 * `budgetWindowKey()` 在 `nucleus/budget.ts`（快照的幂等键组成部分，H 域要用同一份）。
 * 契约只下发**给人看的**窗口起止与天数，不下发那个内部键。
 */

import { z } from 'zod';
import { BudgetTotalKey } from '../constants/index.js';
import { BudgetLevel, BudgetPeriod } from '../enums/plan.js';
import type { BudgetEventState } from '../nucleus/budget.js';
import type { Cents } from '../types/brand.js';
import { DayString } from './pagination.js';

/** 周期取值（🔴 由既有枚举**派生**，不手抄一份 —— `AC-G1.1`「枚举与规格一致」） */
const PERIOD_VALUES = [
  BudgetPeriod.Monthly,
  BudgetPeriod.Quarterly,
  BudgetPeriod.Yearly,
  BudgetPeriod.Event,
] as const;

/** 总预算占位键的**对外常量**（端侧要拿它判断"哪张卡是总预算"，禁自写字面量） */
export { BudgetTotalKey };

// ───────────────────────────── 请求 ─────────────────────────────

/**
 * `POST /api/budgets` —— 建一条预算（分类预算，或总预算用占位键）。
 *
 * 🔴 **金额的正负、`event` 的起止都**不在这里校验** —— 它们由内核
 * `checkBudgetShape()` 判定（唯一判定处，端侧表单转发同一文案）。
 * 这里只做**形状**（类型 / 整分），不做**口径**（铁律 24）。
 */
export const CreateBudgetRequestSchema = z.object({
  /** 分类 id，或总预算占位键 {@link BudgetTotalKey} */
  categoryId: z.string().trim().min(1, '分类不得为空'),
  period: z.enum(PERIOD_VALUES),
  /** 整数分（正负由内核判） */
  amount: z.number().int('预算金额必须是整数分'),
  /** `event` 必填；其余周期忽略 */
  startDate: DayString.nullish(),
  endDate: DayString.nullish(),
});
export type CreateBudgetRequest = z.infer<typeof CreateBudgetRequestSchema>;

/**
 * `PATCH /api/budgets/:id` —— 改预算。
 *
 * 🔴 **不含 `categoryId` / `period`**（见文件头第 3 条）。
 *
 * ⚠️ **档位可回落**（`AC-G2.8`）：调高 `amount` 后档位回到 `safe` 是**口径允许**的，
 * 服务端**不因此推送**（推送时机归 H 域，且"回落不推送"）。
 */
export const UpdateBudgetRequestSchema = z.object({
  amount: z.number().int('预算金额必须是整数分').optional(),
  /** 停用 ⇒ **不进预警、不入快照**（`AC-G1.26` / `AC-G2.15`） */
  enabled: z.boolean().optional(),
  startDate: DayString.nullish(),
  endDate: DayString.nullish(),
});
export type UpdateBudgetRequest = z.infer<typeof UpdateBudgetRequestSchema>;

/** 批量创建的单条（每个分类一个金额） */
export const BatchBudgetItemSchema = z.object({
  categoryId: z.string().trim().min(1, '分类不得为空'),
  amount: z.number().int('预算金额必须是整数分'),
});
export type BatchBudgetItem = z.infer<typeof BatchBudgetItemSchema>;

/**
 * `POST /api/budgets/batch` —— 批量创建（`AC-G1.25`）。
 *
 * 🔴 **幂等跳过**：同分类同周期已存在 ⇒ **不报错、不覆盖**，放进 `skipped` 明细返回。
 */
export const BatchCreateBudgetRequestSchema = z.object({
  period: z.enum(PERIOD_VALUES),
  startDate: DayString.nullish(),
  endDate: DayString.nullish(),
  items: z.array(BatchBudgetItemSchema).min(1, '至少要选一个分类').max(100),
});
export type BatchCreateBudgetRequest = z.infer<typeof BatchCreateBudgetRequestSchema>;

/**
 * `GET /api/budgets/suggestions?categoryId=&period=` —— 三档建议的入参（`AC-G1.15`）。
 *
 * ⚠️ 走 **query string** ⇒ 两个字段都是字符串形式的 id / 枚举值，不需要 `z.coerce`。
 */
export const BudgetSuggestionQuerySchema = z.object({
  categoryId: z.string().trim().min(1, '分类不得为空'),
  period: z.enum(PERIOD_VALUES),
});
export type BudgetSuggestionQuery = z.infer<typeof BudgetSuggestionQuerySchema>;

// ───────────────────────────── 视图 ─────────────────────────────

/** 窗口（**半开区间** `[start, end)` —— 契约里也把这一点写在类型上，避免端侧当闭区间用） */
export interface BudgetWindowView {
  /** 含 */
  start: string;
  /** 不含 */
  end: string;
  totalDays: number;
  elapsedDays: number;
}

/** 派生量 —— 🔴 **全部由服务端算好**（端侧零口径，见文件头第 2 条） */
export interface BudgetProgressView {
  /** 已用（**含**待缴·未付 —— 口径唯一，不为它开例外） */
  spent: Cents;
  /** 其中「待缴·未付」部分（仅用于标注；`> 0` 才显示） */
  unpaidPending: Cents;
  /** `amount − spent`，**不截断**（可为负 ⇒ 超支） */
  remaining: Cents;
  /** 已用%（`E4`，`10000` = 100%） */
  usedE4: number;
  /** 时间进度（`E4`） */
  timeProgressE4: number;
  /** 偏离 = 已用% − 时间进度%（负 = 偏慢） */
  deviationE4: number;
  /** 线性外推预计；🔴 时间进度 < 10% 时 = `null`（**不显示**，不是显示 0） */
  projectedEnd: Cents | null;
}

/**
 * 一张预算卡（总预算 / 分类预算 / 事件预算**同一形状**）。
 *
 * ⚠️ `categoryId === BudgetTotalKey` ⇔ `isTotal === true`；两者都下发，
 * 是为了让端侧**不必**记住那个保留字（`isTotal` 才是端侧该用的判据）。
 */
export interface BudgetCardView {
  id: string;
  categoryId: string;
  /** 展示名（服务端 join；总预算 ⇒ 「总预算」） */
  categoryName: string;
  /** 🔴 端侧判断"这是不是总预算卡"**只用这个** */
  isTotal: boolean;
  period: BudgetPeriod;
  /** 整数分（本币）；预算恒为本币（规格 §5） */
  amount: Cents;
  /** 本币币种码 —— 卡面每个金额都用它渲染（`DR-036`：金额必带币种） */
  currency: string;
  enabled: boolean;
  /** `event` 的起止（闭区间，给人看的）；其余周期为 `null` */
  startDate: string | null;
  endDate: string | null;
  window: BudgetWindowView;
  /** 仅 `event` 有值：未开始 / 进行中 / 已结束 */
  eventState: BudgetEventState | null;
  /** 档位（`AC-G2.6`：`over` 优先） */
  level: BudgetLevel;
  /** 本次是否参与判档（停用 / 非正预算 / `event` 未开始或已结束 ⇒ `false`） */
  evaluated: boolean;
  /**
   * 🔴 **本卡的金额里有腿因缺该日汇率而未折算**（`AC-G1.31` · 架构 §5.4 铁律 5）。
   *
   * `true` ⇒ 端侧**必须**标注「本币合计不完整」。缺汇率**不静默、不按 1:1 冒充**。
   */
  fxIncomplete: boolean;
  progress: BudgetProgressView;
}

/** 成员维度的一行（🔴 **只做展示**：不设上限、不排名、不评价 —— `DR-011` §7） */
export interface BudgetMemberView {
  userId: string;
  /** 展示名 */
  name: string;
  /** 窗口内该成员**归属**的支出合计（本币分） */
  spent: Cents;
  /** 是不是当前登录用户（端侧用来加「（我）」，**不参与排序**） */
  isSelf: boolean;
}

/**
 * `GET /api/budgets` —— 预算列表 + 窗口内的成员维度（**一次请求拿齐一屏**）。
 *
 * 依据：G 域 PRD §4.5（派生指标）· §4.8（成员维度）· §4.3（软提示）· `AC-G1.29`（排序）。
 * 🔴 **排序由服务端定**（`AC-G1.29`：已超 > 接近 > 正常，同档按已用% 降序，总预算置顶）
 * —— 端侧**不重排**（重排就是第二份排序口径）。
 */
export interface BudgetListResult {
  items: readonly BudgetCardView[];
  baseCurrency: string;
  /** 总预算卡的 id；`null` = 尚未设总预算（卡面显示引导） */
  totalBudgetId: string | null;
  /** 分类预算金额之和（软提示**左侧**的数） */
  categorySum: Cents;
  /** 总预算金额；`null` = 未设 */
  totalAmount: Cents | null;
  /** 🔴 分类之和 > 总预算 —— **软提示，不拦截**（`AC-G1.28` / `DR-011` §5） */
  overAllocated: boolean;
  /** 成员维度（🔴 单人账本 ⇒ **空数组**，端侧整块隐藏 —— `AC-G1.23`） */
  members: readonly BudgetMemberView[];
  /** 「共同（未归因）」合计 */
  unattributed: Cents;
  /**
   * 🔴 本屏**任一**卡片存在"缺汇率未折算"（= `items.some(c => c.fxIncomplete)`）。
   *
   * 冗余下发是为了让端侧**不必自己遍历**（扫描数组也是"端侧算口径"的雏形）。
   */
  fxIncomplete: boolean;
}

/**
 * `GET /api/budgets/suggestions` —— 新建预算的**三档建议**（`AC-G1.15`：单接口返回）。
 *
 * 🔴 **无数据 ⇒ `null`**（该档**隐藏**），**不是 0**
 * —— `AC-G1.18` 明文「无收入数据时「收入 30%」档隐藏（不显示 0/null）」：
 * 显示 0 会让用户以为"建议就是 0 元"，而 `null` 让端侧**根本没有可渲染的数**。
 */
export interface BudgetSuggestionView {
  categoryId: string;
  period: BudgetPeriod;
  /** 档 1：该分类在**上一个同长度窗口**的花费 */
  lastWindow: Cents | null;
  /** 档 2：**近 3 个同窗口的算术均值** —— 🔴 默认推荐（`AC-G1.17`） */
  recentAverage: Cents | null;
  /** 档 3：同期**收入合计 × 30%**（`BudgetThreshold.IncomeSuggestE4`） */
  incomeShare: Cents | null;
  /** 默认推荐档；三档全空 ⇒ `null`（端侧不预填） */
  recommended: BudgetSuggestionTier | null;
}

/** 三档的标识（端侧用它决定"哪一档是默认选中"，**不猜数值大小**） */
export const BudgetSuggestionTier = {
  LastWindow: 'lastWindow',
  RecentAverage: 'recentAverage',
  IncomeShare: 'incomeShare',
} as const;
export type BudgetSuggestionTier =
  (typeof BudgetSuggestionTier)[keyof typeof BudgetSuggestionTier];

/**
 * 删除结果（`DELETE /api/budgets/:id`）。
 *
 * ⚠️ 与 `AccountDeletedView` / `PolicyDeletedView` / `ScheduleDeletedView` 同处置：
 * **形状定在契约层**，端侧禁自写（㉝ 批教训）。
 */
export interface BudgetDeletedView {
  deleted: true;
  id: string;
}

/**
 * `POST /api/budgets/batch` 的结果（`AC-G1.25`）。
 *
 * 🔴 **跳过项要带原因**：只说"跳过了 3 个"用户无从判断是重复还是校验失败。
 */
export interface BudgetBatchCreateResult {
  created: readonly BudgetCardView[];
  skipped: readonly { categoryId: string; reason: string }[];
}
